import { Chart, DatasetController, } from 'chart.js';
import { toFont } from 'chart.js/helpers';
import layout from 'd3-cloud';
import { WordElement } from '../elements';
import patchController from './patchController';
function rnd(seed = Date.now()) {
    let s = typeof seed === 'number' ? seed : Array.from(seed).reduce((acc, v) => acc + v.charCodeAt(0), 0);
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}
export class WordCloudController extends DatasetController {
    constructor() {
        super(...arguments);
        this.wordLayout = layout()
            .text((d) => d.text)
            .padding((d) => d.options.padding)
            .rotate((d) => d.options.rotate)
            .font((d) => d.options.family)
            .fontSize((d) => d.options.size)
            .fontStyle((d) => d.options.style)
            .fontWeight((d) => { var _a; return (_a = d.options.weight) !== null && _a !== void 0 ? _a : 1; });
        this.rand = Math.random;
    }
    update(mode) {
        super.update(mode);
        this.rand = rnd(this.chart.id);
        const meta = this._cachedMeta;
        const elems = (meta.data || []);
        this.updateElements(elems, 0, elems.length, mode);
    }
    updateElements(elems, start, count, mode) {
        var _a, _b, _c, _d;
        this.wordLayout.stop();
        const xScale = this._cachedMeta.xScale;
        const yScale = this._cachedMeta.yScale;
        const w = xScale.right - xScale.left;
        const h = yScale.bottom - yScale.top;
        const labels = this.chart.data.labels;
        const words = [];
        for (let i = start; i < start + count; i += 1) {
            const o = this.resolveDataElementOptions(i, mode);
            if (o.rotate == null) {
                o.rotate = WordElement.computeRotation(o, this.rand);
            }
            const properties = {
                options: { ...toFont(o), ...o },
                x: (_b = (_a = this._cachedMeta.xScale) === null || _a === void 0 ? void 0 : _a.getPixelForDecimal(0.5)) !== null && _b !== void 0 ? _b : 0,
                y: (_d = (_c = this._cachedMeta.yScale) === null || _c === void 0 ? void 0 : _c.getPixelForDecimal(0.5)) !== null && _d !== void 0 ? _d : 0,
                width: 10,
                height: 10,
                scale: 1,
                index: i,
                text: labels[i],
            };
            words.push(properties);
        }
        if (mode === 'reset') {
            words.forEach((tag) => {
                this.updateElement(elems[tag.index], tag.index, tag, mode);
            });
            return;
        }
        this.wordLayout.random(this.rand).words(words);
        const run = (factor = 1, tries = 3) => {
            this.wordLayout
                .size([w * factor, h * factor])
                .on('end', (tags, bounds) => {
                if (tags.length < labels.length) {
                    if (tries > 0) {
                        run(factor * 1.2, tries - 1);
                        return;
                    }
                    console.warn('cannot fit all text elements in three tries');
                }
                const wb = bounds[1].x - bounds[0].x;
                const hb = bounds[1].y - bounds[0].y;
                const dsOptions = this.options;
                const scale = dsOptions.fit ? Math.min(w / wb, h / hb) : 1;
                const indices = new Set(labels.map((_, i) => i));
                tags.forEach((tag) => {
                    indices.delete(tag.index);
                    this.updateElement(elems[tag.index], tag.index, {
                        options: tag.options,
                        scale,
                        x: xScale.left + scale * tag.x + w / 2,
                        y: yScale.top + scale * tag.y + h / 2,
                        width: scale * tag.width,
                        height: scale * tag.height,
                        text: tag.text,
                    }, mode);
                });
                indices.forEach((i) => this.updateElement(elems[i], i, { scale: 0 }, mode));
            })
                .start();
        };
        run();
    }
    draw() {
        const elements = this._cachedMeta.data;
        const { ctx } = this.chart;
        elements.forEach((elem) => elem.draw(ctx));
    }
    getLabelAndValue(index) {
        const r = super.getLabelAndValue(index);
        const labels = this.chart.data.labels;
        r.label = labels[index];
        return r;
    }
}
WordCloudController.id = 'wordCloud';
WordCloudController.defaults = {
    datasets: {
        fit: true,
        animation: {
            colors: {
                properties: ['color', 'strokeStyle'],
            },
            numbers: {
                properties: ['x', 'y', 'size', 'rotate'],
            },
        },
    },
    maintainAspectRatio: false,
    dataElementType: WordElement.id,
};
WordCloudController.overrides = {
    scales: {
        x: {
            type: 'linear',
            min: -1,
            max: 1,
            display: false,
        },
        y: {
            type: 'linear',
            min: -1,
            max: 1,
            display: false,
        },
    },
};
export class WordCloudChart extends Chart {
    constructor(item, config) {
        super(item, patchController('wordCloud', config, WordCloudController, WordElement));
    }
}
WordCloudChart.id = WordCloudController.id;
//# sourceMappingURL=WordCloudController.js.map